import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionList,
    Flex,
    Tab,
    TabGroup,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
    TabList,
    TabPanel,
    TabPanels,
    Text,
    Title,
} from '@tremor/react'
import { useState } from 'react'
import ContactUs from '../../components/ContactUs'
import Calculator from './calculator'
import { ModelVMType } from '../../api/api'

interface IVM {
    id: ModelVMType
    cpu: number
    ram: number
    shared: boolean
    gpu: boolean
    priceOnDemand?: number
    priceOneMonth?: number
    priceSixMonths?: number
}

export const onDemandVMs: IVM[] = [
    {
        id: ModelVMType.VMTypeBurstable2Core4GB,
        cpu: 2,
        ram: 4,
        shared: true,
        gpu: false,
        priceOnDemand: 0.0577,
    },
    {
        id: ModelVMType.VMTypeBurstable4Core16GB,
        cpu: 4,
        ram: 16,
        shared: true,
        gpu: false,
        priceOnDemand: 0.2307,
    },
    {
        id: ModelVMType.VMTypeMemoryOptimized8Core32GB,
        cpu: 8,
        ram: 32,
        shared: false,
        gpu: false,
        priceOnDemand: 0.541,
    },
    {
        id: ModelVMType.VMTypeComputeOptimized8Core16GB,
        cpu: 8,
        ram: 16,
        shared: false,
        gpu: false,
        priceOnDemand: 0.486,
    },
    {
        id: ModelVMType.VMTypeGraphicOptimized8Core32GB,
        cpu: 8,
        ram: 32,
        shared: false,
        gpu: true,
        priceOnDemand: 1.153,
    },
    {
        id: ModelVMType.VMTypeMemoryIntensive8Core64GB,
        cpu: 8,
        ram: 64,
        shared: false,
        gpu: false,
        priceOnDemand: 0.713,
    },
    {
        id: ModelVMType.VMTypeMemoryIntensive16Core128GB,
        cpu: 16,
        ram: 128,
        shared: false,
        gpu: false,
        priceOnDemand: 1.427,
    },
]

export const generateVMs = () => {
    const list: IVM[] = []
    onDemandVMs.forEach((v) => {
        let { priceOnDemand, priceOneMonth, priceSixMonths } = v

        priceSixMonths = (priceOnDemand || 0) * 0.38 * 0.75
        priceOneMonth = (priceOnDemand || 0) * 0.38 * 0.8
        priceOnDemand = (priceOnDemand || 0) * 0.38 * 0.85

        list.push({
            id: v.id,
            cpu: v.cpu,
            ram: v.ram,
            shared: v.shared,
            gpu: v.gpu,
            priceOnDemand,
            priceOneMonth,
            priceSixMonths,
        })
    })

    return list.sort((a, b) => {
        if (a.priceOnDemand !== undefined && b.priceOnDemand !== undefined) {
            return a.priceOnDemand - b.priceOnDemand
        }
        if (a.priceOneMonth !== undefined && b.priceOneMonth !== undefined) {
            return a.priceOneMonth - b.priceOneMonth
        }
        if (a.priceSixMonths !== undefined && b.priceSixMonths !== undefined) {
            return a.priceSixMonths - b.priceSixMonths
        }

        if (a.cpu !== b.cpu) {
            return a.cpu - b.cpu
        }
        if (a.ram !== b.ram) {
            return a.ram - b.ram
        }
        return 0
    })
}

interface IPricing {
    factor?: number
    constantPrice?: number
    constantPrice2?: number
    hasIpCost?: boolean
    hasNetworkCost?: boolean
}

function PricingTab({
    factor = 1.0,
    constantPrice = 0.0,
    constantPrice2 = 0.0,
    hasIpCost = true,
    hasNetworkCost = true,
}: IPricing) {
    return (
        <AccordionList>
            <Accordion defaultOpen>
                <AccordionHeader className="font-extrabold">
                    Calculator
                </AccordionHeader>
                <AccordionBody>
                    <Calculator
                        factor={factor}
                        constantPrice={constantPrice}
                        constantPrice2={constantPrice2}
                        hasIpCost={hasIpCost}
                        hasNetworkCost={hasNetworkCost}
                    />
                </AccordionBody>
            </Accordion>
            <Accordion>
                <AccordionHeader className="font-extrabold">
                    Compute
                </AccordionHeader>
                <AccordionBody>
                    <Table className="w-full">
                        <TableHead>
                            <TableRow>
                                <TableHeaderCell className="text-center">
                                    CPU (cores)
                                </TableHeaderCell>
                                <TableHeaderCell className="text-center">
                                    Memory (GB)
                                </TableHeaderCell>
                                <TableHeaderCell className="text-center">
                                    Shared CPU
                                </TableHeaderCell>
                                <TableHeaderCell className="text-center">
                                    GPU
                                </TableHeaderCell>
                                <TableHeaderCell className="text-center">
                                    On Demand
                                </TableHeaderCell>
                                <TableHeaderCell className="text-center">
                                    1-Month
                                </TableHeaderCell>
                                <TableHeaderCell className="text-center">
                                    6-Months
                                </TableHeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {generateVMs().map((v) => (
                                <TableRow className="text-black">
                                    <TableCell className="text-center">
                                        {v.cpu}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {v.ram}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {v.shared ? (
                                            <span className="font-bold">
                                                Yes
                                            </span>
                                        ) : (
                                            'No'
                                        )}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {v.gpu ? (
                                            <span className="font-bold">
                                                Yes
                                            </span>
                                        ) : (
                                            'No'
                                        )}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {v.priceOnDemand ? (
                                            <>
                                                <span className="font-bold">
                                                    $
                                                    {(
                                                        v.priceOnDemand *
                                                        (factor || 1)
                                                    )?.toFixed(4)}
                                                </span>{' '}
                                                OMR
                                            </>
                                        ) : (
                                            <span className="text-gray-400">
                                                Not available
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {v.priceOneMonth ? (
                                            <>
                                                <span className="font-bold">
                                                    $
                                                    {(
                                                        v.priceOneMonth *
                                                        (factor || 1)
                                                    )?.toFixed(4)}
                                                </span>{' '}
                                                OMR
                                            </>
                                        ) : (
                                            <span className="text-gray-400">
                                                Not available
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {v.priceSixMonths ? (
                                            <>
                                                <span className="font-bold">
                                                    $
                                                    {(
                                                        v.priceSixMonths *
                                                        (factor || 1)
                                                    )?.toFixed(4)}
                                                </span>{' '}
                                                OMR
                                            </>
                                        ) : (
                                            <span className="text-gray-400">
                                                Not available
                                            </span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </AccordionBody>
            </Accordion>
            <Accordion>
                <AccordionHeader className="font-extrabold">
                    Data Transfer
                </AccordionHeader>
                <AccordionBody>
                    <Text>
                        Transfering data (in and out) is{' '}
                        <span className="font-bold">0.032623 OMR per GB</span>
                    </Text>
                </AccordionBody>
            </Accordion>
            <Accordion>
                <AccordionHeader className="font-extrabold">
                    IP address
                </AccordionHeader>
                <AccordionBody>
                    <Table className="w-full">
                        <TableHead>
                            <TableRow>
                                <TableHeaderCell>Type</TableHeaderCell>
                                <TableHeaderCell>Price</TableHeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="text-black">
                            <TableRow>
                                <TableCell>IPv4</TableCell>
                                <TableCell>0.002584 OMR per hour</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell>Shared IP*</TableCell>
                                <TableCell>Free</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                    <Text className="!text-xs">
                        * Requests will be routed based on the domain name (HTTP
                        & HTTPS only)
                    </Text>
                </AccordionBody>
            </Accordion>
            <Accordion>
                <AccordionHeader className="font-extrabold">
                    Volume
                </AccordionHeader>
                <AccordionBody>
                    <Table className="w-full">
                        <TableHead>
                            <TableRow>
                                <TableHeaderCell>Volume Type</TableHeaderCell>
                                <TableHeaderCell>On Demand</TableHeaderCell>
                                <TableHeaderCell>1-Month</TableHeaderCell>
                                <TableHeaderCell>6-Months</TableHeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="text-black">
                            <TableRow>
                                <TableCell>SSD</TableCell>
                                <TableCell>0.000087 OMR per GB-hour</TableCell>
                                <TableCell>0.000082 OMR per GB-hour</TableCell>
                                <TableCell>0.000077 OMR per GB-hour</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </AccordionBody>
            </Accordion>
        </AccordionList>
    )
}

export default function PricingPage() {
    const [tabIndex, setTabIndex] = useState(0)
    const [contactUsVisible, setContactUsVisible] = useState(false)

    const onTabIndexChanged = (v: number) => {
        if (v < 6) {
            setTabIndex(v)
        } else {
            setContactUsVisible(true)
        }
    }

    return (
        <Flex
            className="p-4 bg-gray-50 w-auto min-w-[1000px] max-w-fit mt-4 rounded-md"
            flexDirection="col"
            justifyContent="start"
            alignItems="start"
        >
            <Flex flexDirection="row" justifyContent="between" className="mb-4">
                <Title className="text-teal-600">Pricing</Title>
            </Flex>

            <TabGroup index={tabIndex} onIndexChange={onTabIndexChanged}>
                <TabList>
                    <Tab>Virtual Machine</Tab>
                    <Tab>Managed Kubernetes</Tab>
                    <Tab>OpenSearch</Tab>
                    <Tab>PostgreSQL</Tab>
                    <Tab>MySQL</Tab>
                    <Tab>Redis</Tab>
                    <Tab>Odoo</Tab>
                    <Tab>LLM</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel>
                        <PricingTab factor={1} />
                    </TabPanel>
                    <TabPanel>
                        <PricingTab
                            constantPrice={0.038}
                            constantPrice2={0.057}
                            factor={1}
                        />
                    </TabPanel>
                    <TabPanel>
                        <PricingTab
                            factor={1.7}
                            hasIpCost={false}
                            hasNetworkCost={false}
                        />
                    </TabPanel>
                    <TabPanel>
                        <PricingTab
                            factor={2}
                            hasIpCost={false}
                            hasNetworkCost={false}
                        />
                    </TabPanel>
                    <TabPanel>
                        <PricingTab
                            factor={2}
                            hasIpCost={false}
                            hasNetworkCost={false}
                        />
                    </TabPanel>
                    <TabPanel>
                        <PricingTab
                            factor={1.95}
                            hasIpCost={false}
                            hasNetworkCost={false}
                        />
                    </TabPanel>
                    <TabPanel />
                    <TabPanel />
                </TabPanels>
            </TabGroup>
            <ContactUs
                show={contactUsVisible}
                onClose={() => setContactUsVisible(false)}
            />
        </Flex>
    )
}
